import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminReset — single-use owner-recovery flow.
 *
 * Route: `/admin/reset?t=<opaque-token>`
 *
 * The page verifies the token via `GET /api/admin-recovery/verify`, then
 * requires the owner to:
 *   1) confirm the admin email (bound-factor — a URL alone is not enough),
 *   2) set a new password (masked, twice), which is bcrypt-hashed on the
 *      server before storage.
 *
 * The token, both password fields, and any raw values are never persisted
 * to any browser storage and are cleared from state immediately on
 * successful submission.
 */
export default function AdminReset() {
    const [params] = useSearchParams();
    const token = params.get("t") || "";
    const navigate = useNavigate();

    const [verifying, setVerifying] = useState(true);
    const [valid, setValid] = useState(false);
    const [reason, setReason] = useState("");
    const [hint, setHint] = useState("");
    const [email, setEmail] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            if (!token) {
                setVerifying(false);
                setReason("missing_token");
                return;
            }
            try {
                const { data } = await api.get(
                    `/admin-recovery/verify?t=${encodeURIComponent(token)}`
                );
                setValid(!!data?.valid);
                setReason(data?.reason || "");
                setHint(data?.email_hint || "");
            } catch (_e) {
                setValid(false);
                setReason("network");
            } finally {
                setVerifying(false);
            }
        })();
    }, [token]);

    async function handleSubmit(e) {
        e.preventDefault();
        if (pw !== pw2) {
            toast.error("Passwords do not match");
            return;
        }
        if (pw.length < 8) {
            toast.error("Use at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin-recovery/reset", {
                token,
                email: email.trim().toLowerCase(),
                new_password: pw,
                confirm_password: pw2,
            });
            // Wipe every trace of the values from state.
            setPw("");
            setPw2("");
            setEmail("");
            setDone(true);
            toast.success("Password updated. Please sign in.");
            setTimeout(() => navigate("/login", { state: { from: "/setup/instagram" } }), 1500);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Reset failed";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]" data-testid="admin-reset-page">
            <Navbar />
            <section className="pt-28 md:pt-36 pb-24 px-6 md:px-12 max-w-3xl mx-auto">
                <p className="overline overline-primary mb-4">Admin · Password Recovery</p>
                <h1 className="font-display text-4xl md:text-5xl leading-[1.05] mb-6">
                    Set a New Admin Password
                </h1>

                {verifying && (
                    <div className="flex items-center gap-2 text-zinc-500">
                        <Loader2 size={16} className="animate-spin" /> Verifying link…
                    </div>
                )}

                {!verifying && !valid && (
                    <div className="border border-amber-400/40 bg-amber-400/5 px-5 py-4 text-[14px]" data-testid="reset-invalid">
                        This recovery link is not valid ({reason || "unknown"}). Ask the developer for a fresh single-use link.
                        <div className="mt-4">
                            <Link to="/login" className="underline">Return to sign in</Link>
                        </div>
                    </div>
                )}

                {!verifying && valid && !done && (
                    <>
                        <p className="text-[15px] leading-[1.7] text-[var(--crewz-text)]/80 mb-8 max-w-2xl">
                            This is the website's <strong>internal admin password</strong> — separate from your
                            Gmail, Instagram, Meta, or Emergent account. The new value is bcrypt-hashed on
                            the server before storage and is not logged.
                        </p>
                        {hint && (
                            <p className="text-[13px] text-zinc-500 mb-6" data-testid="reset-email-hint">
                                Recovery bound to <code className="font-mono">{hint}</code>
                            </p>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5 max-w-lg" autoComplete="off">
                            <label className="block">
                                <span className="block text-[12px] uppercase tracking-[0.22em] text-zinc-400 mb-2">
                                    Confirm admin email
                                </span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@crewznation.com"
                                    className="w-full bg-transparent border border-white/15 focus:border-[var(--crewz-gold)] outline-none px-4 py-3 font-mono text-[14px] tracking-tight"
                                    autoComplete="off"
                                    data-testid="reset-email-input"
                                    required
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[12px] uppercase tracking-[0.22em] text-zinc-400 mb-2">
                                    New password (min 8 characters)
                                </span>
                                <input
                                    type="password"
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                    className="w-full bg-transparent border border-white/15 focus:border-[var(--crewz-gold)] outline-none px-4 py-3 font-mono text-[14px] tracking-tight"
                                    autoComplete="new-password"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    minLength={8}
                                    maxLength={72}
                                    data-testid="reset-new-password"
                                    required
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[12px] uppercase tracking-[0.22em] text-zinc-400 mb-2">
                                    Confirm new password
                                </span>
                                <input
                                    type="password"
                                    value={pw2}
                                    onChange={(e) => setPw2(e.target.value)}
                                    className="w-full bg-transparent border border-white/15 focus:border-[var(--crewz-gold)] outline-none px-4 py-3 font-mono text-[14px] tracking-tight"
                                    autoComplete="new-password"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    minLength={8}
                                    maxLength={72}
                                    data-testid="reset-confirm-password"
                                    required
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={submitting || !pw || !pw2 || !email}
                                className="inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 text-[13px] tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                                data-testid="reset-submit"
                            >
                                {submitting ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                                {submitting ? "Saving…" : "Set New Password"}
                            </button>
                            <p className="text-[12px] text-zinc-500 flex items-center gap-2">
                                <ShieldCheck size={12} /> The link expires 15 minutes after issue and is single-use.
                            </p>
                        </form>
                    </>
                )}

                {done && (
                    <div className="border border-emerald-400/40 bg-emerald-400/5 px-5 py-4 text-[14px]" data-testid="reset-success">
                        Password updated. Redirecting to sign in…
                    </div>
                )}
            </section>
            <Footer />
        </div>
    );
}
